/**
 * Соединение оверлея с hub.
 *
 * Главное свойство: при разрыве транспорт НИЧЕГО не сообщает
 * о потере состояния и ничего не очищает. Оверлей продолжает
 * рисовать то, что нарисовал, пока идёт переподключение —
 * зритель не должен видеть чёрный экран из-за сетевого чиха
 * (docs/01).
 *
 * Browser Source в OBS падает и перезапускается — это норма,
 * а не исключение. Транспорт обязан переживать это молча.
 */
import { DEFAULT_BACKOFF, delayFor } from './backoff.js';
const DEFAULT_STABLE_AFTER_MS = 5_000;
/**
 * Предел ожидания открытия.
 *
 * Десять секунд — заведомо больше любого разумного рукопожатия
 * даже на плохой мобильной сети, и заведомо меньше времени, за которое
 * стример успеет заметить пропавший оверлей.
 */
const DEFAULT_CONNECT_TIMEOUT_MS = 10_000;
export class Transport {
    events;
    socket = null;
    state = 'idle';
    attempt = 0;
    timer = null;
    /**
     * Момент открытия или null, если соединение ещё не открывалось.
     *
     * Именно null, а не 0: ноль — допустимая метка времени, и
     * использовать его признаком «никогда» значит однажды принять
     * настоящее открытие за его отсутствие.
     */
    openedAt = null;
    /** Закрыто по нашей команде: переподключаться не нужно. */
    stopped = false;
    url;
    createSocket;
    backoff;
    stableAfterMs;
    connectTimeoutMs;
    schedule;
    unschedule;
    now;
    constructor(options, events = {}) {
        this.events = events;
        this.url = options.url;
        this.createSocket = options.createSocket ?? defaultCreateSocket;
        this.backoff = options.backoff ?? DEFAULT_BACKOFF;
        this.stableAfterMs = options.stableAfterMs ?? DEFAULT_STABLE_AFTER_MS;
        this.connectTimeoutMs = options.connectTimeoutMs ?? DEFAULT_CONNECT_TIMEOUT_MS;
        this.schedule = options.setTimeout ?? ((fn, ms) => setTimeout(fn, ms));
        this.unschedule = options.clearTimeout ?? ((h) => clearTimeout(h));
        this.now = options.now ?? (() => Date.now());
    }
    /** Текущее состояние. */
    getState() {
        return this.state;
    }
    /** Номер текущей попытки переподключения; 0 — соединение живо. */
    getAttempt() {
        return this.attempt;
    }
    /** Открывает соединение. Повторный вызов на живом соединении ничего не делает. */
    connect() {
        if (this.socket || this.state === 'connecting')
            return;
        this.stopped = false;
        this.open();
    }
    /**
     * Закрывает соединение и прекращает попытки.
     * Вызывается при выгрузке страницы.
     */
    close() {
        this.stopped = true;
        this.cancelTimer();
        const socket = this.socket;
        this.socket = null;
        if (socket) {
            this.detach(socket);
            socket.close(1000, '');
        }
        this.setState('closed');
    }
    /**
     * Отправляет сообщение. Возвращает false, если соединения нет.
     *
     * Очередь на время разрыва намеренно НЕ ведётся: оверлей почти
     * ничего не отправляет, а накопленные за минуту сообщения
     * к моменту восстановления уже устарели.
     */
    send(data) {
        if (!this.socket || this.state !== 'open')
            return false;
        try {
            this.socket.send(data);
            return true;
        }
        catch {
            // Сокет мог умереть между проверкой и отправкой
            return false;
        }
    }
    // ─── Внутреннее ─────────────────────────────────────────────────
    open() {
        this.setState(this.attempt === 0 ? 'connecting' : 'reconnecting');
        let socket;
        try {
            socket = this.createSocket(this.url);
        }
        catch {
            // Конструктор WebSocket бросает на неверном адресе.
            // Это не повод сдаваться: адрес мог стать валидным
            // (например, поднялся DNS)
            this.scheduleReconnect();
            return;
        }
        this.socket = socket;
        // Попытка завершается ровно один раз, каким бы путём это
        // ни случилось. Браузер на неудачном подключении шлёт error
        // И close, и без этого флага переподключение назначалось бы
        // дважды: счётчик попыток поехал бы вдвое, а оверлей начал бы
        // стучаться чаще, чем рассчитано.
        let settled = false;
        let connectTimer = null;
        const cancelConnectTimer = () => {
            if (connectTimer !== null) {
                this.unschedule(connectTimer);
                connectTimer = null;
            }
        };
        /** Снимает сокет с учёта. Возвращает false, если уже снят. */
        const settle = () => {
            if (settled)
                return false;
            settled = true;
            cancelConnectTimer();
            this.detach(socket);
            if (this.socket === socket)
                this.socket = null;
            return true;
        };
        /** Общий хвост неудачи: либо молчим, либо пробуем снова. */
        const failed = () => {
            if (this.stopped) {
                this.setState('closed');
                return;
            }
            this.openedAt = null;
            this.scheduleReconnect();
        };
        // Сокет, который приняли и о котором забыли, не шлёт вообще
        // ничего: ни open, ни close, ни error. Так выглядит зависший
        // прокси или полуоткрытое соединение. Без этого таймера оверлей
        // остался бы в «подключаюсь» навсегда — то есть молча пропал бы
        // из эфира, и никто бы не понял почему.
        connectTimer = this.schedule(() => {
            connectTimer = null;
            if (!settle())
                return;
            try {
                socket.close(1000, '');
            }
            catch {
                // Сокет мог умереть сам — нам всё равно
            }
            failed();
        }, this.connectTimeoutMs);
        socket.onopen = () => {
            // Соединение состоялось: дальше им распоряжается onclose,
            // а таймер подключения больше не нужен
            cancelConnectTimer();
            this.openedAt = this.now();
            this.setState('open');
            this.events.onOpen?.();
        };
        socket.onmessage = (event) => {
            this.events.onMessage?.(event.data);
        };
        socket.onerror = () => {
            // Спецификация обещает close вслед за error, и браузер так
            // и делает — но не всякая реализация. WebSocket в Node (undici)
            // на неудачном подключении шлёт ТОЛЬКО error и оставляет
            // readyState в CONNECTING. Транспорт, ждущий close, застревал
            // в «подключаюсь» навсегда: измерено, а не предположено.
            //
            // На уже открытом соединении error ничего не решает — там
            // хозяин onclose.
            if (this.openedAt !== null)
                return;
            if (!settle())
                return;
            failed();
        };
        socket.onclose = () => {
            if (!settle())
                return;
            // Счётчик сбрасывается только если соединение продержалось.
            // Иначе хлопающий сервер обнуляет его на каждой итерации,
            // и оверлей долбится в него по полсекунды.
            if (this.openedAt !== null && this.now() - this.openedAt >= this.stableAfterMs) {
                this.attempt = 0;
            }
            // Проверка «нас закрыли» живёт в failed() — одна на все три
            // пути неудачи. Своя копия здесь была бы недостижимой: close()
            // снимает обработчики, и этот код после него уже не вызовут.
            failed();
        };
    }
    scheduleReconnect() {
        this.attempt += 1;
        this.setState('reconnecting');
        const delay = delayFor(this.attempt, this.backoff);
        this.timer = this.schedule(() => {
            this.timer = null;
            if (!this.stopped)
                this.open();
        }, delay);
    }
    cancelTimer() {
        if (this.timer !== null) {
            this.unschedule(this.timer);
            this.timer = null;
        }
    }
    /**
     * Снимает обработчики с сокета.
     *
     * Без этого мёртвый сокет продолжает дёргать наши колбэки: при
     * серии переподключений старые соединения досылали бы события
     * уже после того, как транспорт перешёл к новому, и счётчик
     * попыток поехал бы.
     *
     * Правило no-param-reassign здесь неприменимо: изменение
     * переданного сокета — ровно то, ради чего функция существует.
     */
    detach(socket) {
        /* eslint-disable no-param-reassign */
        socket.onopen = null;
        socket.onclose = null;
        socket.onerror = null;
        socket.onmessage = null;
        /* eslint-enable no-param-reassign */
    }
    lastReportedAttempt = -1;
    /**
     * Сообщает о смене состояния.
     *
     * Событие шлётся и при неизменном состоянии, если сменился номер
     * попытки: панель показывает «переподключение, попытка 5», и без
     * этого счётчик замер бы на единице, хотя оверлей продолжает
     * стучаться.
     */
    setState(next) {
        if (this.state === next && this.lastReportedAttempt === this.attempt)
            return;
        this.state = next;
        this.lastReportedAttempt = this.attempt;
        this.events.onStateChange?.(next, this.attempt);
    }
}
function defaultCreateSocket(url) {
    const socket = new WebSocket(url);
    // ДВОИЧНЫЕ КАДРЫ — ArrayBuffer, А НЕ Blob.
    //
    // По умолчанию браузер отдаёт Blob, а его содержимое читается
    // асинхронно: на кадрах звука это полсотни промисов в секунду
    // и лишняя задержка ровно там, где её и так считают по крохам.
    // ArrayBuffer приходит готовым.
    socket.binaryType = 'arraybuffer';
    return socket;
}
//# sourceMappingURL=transport.js.map